/**
 * Web UI implementation
 * User interface layer - calls into core PhotoService for business logic
 */

import { Photo, PhotoGroup, isVideo } from '../core/domain.js';
import { IPhotoService } from '../core/photo-service.js';
import { ISelectionService } from '../core/selection-service.js';
import { zipSync } from 'fflate';

/**
 * Configuration for web UI
 */
export interface WebUIConfig {
  containerElement: HTMLElement;
  photoService: IPhotoService;
  selectionService: ISelectionService;
}

// Placeholder image (1x1 gray pixel as data URL)
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="256" height="256"%3E%3Crect width="256" height="256" fill="%23ddd"/%3E%3C/svg%3E';

// Virtual scrolling configuration
const ITEM_MIN_WIDTH_DESKTOP = 200; // Match CSS minmax(200px, 1fr)
const ITEM_MIN_WIDTH_MOBILE = 120; // Match CSS mobile media query
const MOBILE_BREAKPOINT = 480;
const ITEM_ASPECT_RATIO = 1; // Square thumbnails
const GRID_GAP_DESKTOP = 16; // Match CSS gap: 1rem
const GRID_GAP_MOBILE = 8; // Match CSS mobile gap: 0.5rem
const BUFFER_ROWS = 3; // Render extra rows above/below viewport
const HEADER_HEIGHT = 48; // Height of month/year header in pixels

function getItemMinWidth(): number {
  return window.innerWidth <= MOBILE_BREAKPOINT ? ITEM_MIN_WIDTH_MOBILE : ITEM_MIN_WIDTH_DESKTOP;
}

function getGridGap(): number {
  return window.innerWidth <= MOBILE_BREAKPOINT ? GRID_GAP_MOBILE : GRID_GAP_DESKTOP;
}

export class WebUI {
  private container: HTMLElement;
  private photoService: IPhotoService;
  private selectionService: ISelectionService;

  // Virtual scrolling state
  private totalPhotoCount = 0;
  private scrollContainer?: HTMLElement;
  private scrollHandler?: () => void;
  private resizeHandler?: () => void;
  private isRendering = false;
  private thumbnailRefreshTimer?: ReturnType<typeof setTimeout>;

  // Lightbox state
  private currentLightboxIndex = 0;
  private keyboardHandler?: (e: KeyboardEvent) => void;

  // EXIF extraction polling state
  private isPollingExif = false;

  constructor(config: WebUIConfig) {
    this.container = config.containerElement;
    this.photoService = config.photoService;
    this.selectionService = config.selectionService;
    this.initializeUI();
    this.setupScrollHandling();
  }

  /**
   * Load and display photos
   */
  async loadPhotos(): Promise<void> {
    try {
      this.showProgress('Scanning folder...');

      this.totalPhotoCount = await this.photoService.loadPhotos((filesFound) => {
        this.showProgress(`Found ${filesFound} files...`);
      });

      // Render initial viewport
      await this.renderVisibleItems();

      // Start polling for EXIF extraction progress
      this.pollExifProgress();
    } catch (error) {
      console.error('Error loading photos:', error);
      this.showError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Show lightbox for photo at index
   */
  async showLightbox(index: number): Promise<void> {
    this.currentLightboxIndex = index;

    // Create lightbox if it doesn't exist
    let lightbox = document.querySelector('.lightbox') as HTMLElement;
    if (!lightbox) {
      lightbox = this.createLightboxElement();
      document.body.appendChild(lightbox);
    }

    // Show lightbox
    lightbox.style.display = 'flex';

    // Load and display the photo
    await this.updateLightboxImage();

    // Setup keyboard navigation
    this.setupKeyboardNavigation();
  }

  showProgress(message: string): void {
    const grid = this.container.querySelector('.photo-grid');
    if (grid) {
      grid.innerHTML = `<div class="loading">${message}</div>`;
    }
  }

  showError(message: string): void {
    const grid = this.container.querySelector('.photo-grid');
    if (grid) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error';
      errorDiv.textContent = `Error: ${message}`;
      grid.innerHTML = '';
      grid.appendChild(errorDiv);
    }
  }

  private createLightboxElement(): HTMLElement {
    const lightbox = document.createElement('div');
    lightbox.className = 'lightbox';
    lightbox.innerHTML = `
      <button class="lightbox-close" aria-label="Close">&times;</button>
      <button class="lightbox-select" aria-label="Toggle selection" title="Add to download cart (D)">☐</button>
      <button class="lightbox-star" aria-label="Toggle star">☆</button>
      <button class="lightbox-nav lightbox-prev" aria-label="Previous">‹</button>
      <button class="lightbox-nav lightbox-next" aria-label="Next">›</button>
      <div class="lightbox-content">
        <img class="lightbox-image" style="display: none;" alt="" />
        <video class="lightbox-video" controls style="display: none;"></video>
        <div class="lightbox-loading">Loading...</div>
      </div>
    `;

    // Close button
    const closeBtn = lightbox.querySelector('.lightbox-close') as HTMLElement;
    closeBtn.addEventListener('click', () => this.closeLightbox());

    // Selection button
    const selectBtn = lightbox.querySelector('.lightbox-select') as HTMLElement;
    selectBtn.addEventListener('click', () => this.toggleLightboxSelection());

    // Star button
    const starBtn = lightbox.querySelector('.lightbox-star') as HTMLElement;
    starBtn.addEventListener('click', () => this.toggleStar());

    // Navigation buttons
    const prevBtn = lightbox.querySelector('.lightbox-prev') as HTMLElement;
    prevBtn.addEventListener('click', () => this.showPrevPhoto());

    const nextBtn = lightbox.querySelector('.lightbox-next') as HTMLElement;
    nextBtn.addEventListener('click', () => this.showNextPhoto());

    // Close on background click
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) {
        this.closeLightbox();
      }
    });

    return lightbox;
  }

  private async toggleStar(): Promise<void> {
    if (this.currentLightboxIndex === null) return;

    await this.photoService.toggleStar(this.currentLightboxIndex);
    this.updateStarButton();

    // Refresh the grid to show/hide starred indicator on thumbnail
    await this.renderVisibleItems();
  }

  private async toggleLightboxSelection(): Promise<void> {
    const photos = await this.photoService.getPhotos(
      this.currentLightboxIndex,
      this.currentLightboxIndex + 1
    );
    if (photos.length === 0) return;

    this.selectionService.toggleSelection(photos[0].id);
    this.updateSelectButton();
    this.updateCartBadge();
    // Grid checkboxes will update naturally on next scroll/render
  }

  private updateSelectButton(): void {
    const lightbox = document.querySelector('.lightbox');
    if (!lightbox) return;

    const selectBtn = lightbox.querySelector('.lightbox-select') as HTMLElement;
    if (!selectBtn) return;

    this.photoService.getPhotos(this.currentLightboxIndex, this.currentLightboxIndex + 1)
      .then(photos => {
        if (photos.length > 0) {
          const isSelected = this.selectionService.isSelected(photos[0].id);
          selectBtn.textContent = isSelected ? '☑' : '☐';
          selectBtn.classList.toggle('selected', isSelected);
        }
      });
  }

  private updateStarButton(): void {
    const lightbox = document.querySelector('.lightbox');
    if (!lightbox) return;

    const starBtn = lightbox.querySelector('.lightbox-star') as HTMLElement;
    if (!starBtn) return;

    if (this.currentLightboxIndex !== null) {
      this.photoService.getPhotos(this.currentLightboxIndex, this.currentLightboxIndex + 1)
        .then(photos => {
          if (photos.length > 0) {
            starBtn.textContent = photos[0].isStarred ? '★' : '☆';
          }
        });
    }
  }

  private async updateLightboxImage(): Promise<void> {
    const lightbox = document.querySelector('.lightbox');
    if (!lightbox) return;

    const img = lightbox.querySelector('.lightbox-image') as HTMLImageElement;
    const video = lightbox.querySelector('.lightbox-video') as HTMLVideoElement;
    const loading = lightbox.querySelector('.lightbox-loading') as HTMLElement;
    const prevBtn = lightbox.querySelector('.lightbox-prev') as HTMLElement;
    const nextBtn = lightbox.querySelector('.lightbox-next') as HTMLElement;

    // Update star and selection button states
    this.updateStarButton();
    this.updateSelectButton();

    // Hide loading text and video initially
    loading.style.display = 'none';
    video.style.display = 'none';

    // Stop current video if playing
    if (video.src) {
      video.pause();
      video.src = '';
    }

    // Update navigation button visibility
    prevBtn.style.display = this.currentLightboxIndex > 0 ? 'block' : 'none';
    nextBtn.style.display =
      this.currentLightboxIndex < this.totalPhotoCount - 1 ? 'block' : 'none';

    try {
      // Get the current photo (includes thumbnailUrl)
      const photos = await this.photoService.getPhotos(
        this.currentLightboxIndex,
        this.currentLightboxIndex + 1
      );

      if (photos.length === 0) {
        throw new Error('Photo not found');
      }

      const currentPhoto = photos[0];
      const thumbnailUrl = currentPhoto.thumbnailUrl;

      if (isVideo(currentPhoto)) {
        // For videos: show thumbnail as poster while loading
        img.style.display = 'none';

        // Request full-size URL first (before showing video to avoid browser error)
        const fullSizeUrl = await this.photoService.getFullSizeUrl(this.currentLightboxIndex);

        // Now show video with poster and source set together
        if (thumbnailUrl) {
          video.poster = thumbnailUrl;
        }
        video.src = fullSizeUrl;
        video.style.display = 'block';

        // Track which video load this is to ignore stale events
        const expectedSrc = fullSizeUrl;

        // Show video when ready to play
        const onCanPlay = () => {
          // Ignore events from previous video loads
          if (video.src !== expectedSrc) return;
          loading.style.display = 'none';
          video.style.display = 'block';
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('error', onError);
        };

        // Handle video loading errors (e.g., unsupported codec)
        const onError = () => {
          // Ignore events from previous video loads (e.g., clearing src)
          if (video.src !== expectedSrc) return;
          loading.textContent = 'Error: Video format not supported by your browser';
          loading.style.display = 'block';
          video.style.display = 'none';
          video.removeEventListener('canplay', onCanPlay);
          video.removeEventListener('error', onError);
          console.error('Error loading video:', currentPhoto.filePath);
        };

        video.addEventListener('canplay', onCanPlay);
        video.addEventListener('error', onError);
      } else {
        // For images: show thumbnail immediately, then load full-size
        video.style.display = 'none';

        if (thumbnailUrl) {
          // Show thumbnail immediately (will be blurry when scaled up)
          img.src = thumbnailUrl;
          img.alt = `Photo ${this.currentLightboxIndex + 1}`;
          img.style.display = 'block';
        } else {
          // No thumbnail available, show loading text
          loading.style.display = 'block';
          loading.textContent = 'Loading...';
          img.style.display = 'none';
        }

        // Request full-size URL
        const fullSizeUrl = await this.photoService.getFullSizeUrl(this.currentLightboxIndex);

        // Preload the full-size image in background
        const fullSizeImage = new Image();
        fullSizeImage.onload = () => {
          // Replace thumbnail with full-size image
          img.src = fullSizeUrl;
          img.style.display = 'block';
          loading.style.display = 'none';
        };

        fullSizeImage.onerror = () => {
          loading.textContent = 'Error loading image';
          loading.style.display = 'block';
          // Keep showing thumbnail if available, otherwise hide image
          if (!thumbnailUrl) {
            img.style.display = 'none';
          }
          console.error('Error loading image:', currentPhoto.filePath);
        };

        // Start loading full-size
        fullSizeImage.src = fullSizeUrl;
      }
    } catch (error) {
      loading.textContent = 'Error loading media';
      loading.style.display = 'block';
      img.style.display = 'none';
      video.style.display = 'none';
      console.error('Error loading full-size media:', error);
    }
  }

  private showPrevPhoto(): void {
    if (this.currentLightboxIndex > 0) {
      this.currentLightboxIndex--;
      this.updateLightboxImage();
    }
  }

  private showNextPhoto(): void {
    if (this.currentLightboxIndex < this.totalPhotoCount - 1) {
      this.currentLightboxIndex++;
      this.updateLightboxImage();
    }
  }

  private closeLightbox(): void {
    const lightbox = document.querySelector('.lightbox') as HTMLElement;
    if (lightbox) {
      // Stop video if playing
      const video = lightbox.querySelector('.lightbox-video') as HTMLVideoElement;
      if (video && video.src) {
        video.pause();
        video.src = '';
      }

      lightbox.style.display = 'none';
    }

    // Remove keyboard listener
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = undefined;
    }
  }

  private setupKeyboardNavigation(): void {
    // Remove existing handler if any
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }

    // Create new handler
    this.keyboardHandler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          this.closeLightbox();
          break;
        case 'ArrowLeft':
          this.showPrevPhoto();
          break;
        case 'ArrowRight':
          this.showNextPhoto();
          break;
        case ' ':
        case 'Spacebar':
          e.preventDefault(); // Prevent page scroll
          this.toggleStar();
          break;
        case 'd':
        case 'D':
          this.toggleLightboxSelection();
          break;
      }
    };

    document.addEventListener('keydown', this.keyboardHandler);
  }

  private setupScrollHandling(): void {
    // Render immediately on scroll (isRendering guard prevents concurrent renders)
    this.scrollHandler = () => {
      this.renderVisibleItems();
    };

    this.resizeHandler = () => {
      this.renderVisibleItems();
    };
  }

  private async renderVisibleItems(): Promise<void> {
    const grid = this.container.querySelector('.photo-grid') as HTMLElement;
    if (!grid || this.totalPhotoCount === 0) {
      return;
    }

    // Prevent concurrent renders
    if (this.isRendering) {
      return;
    }
    this.isRendering = true;

    try {
      // Attach scroll listener if not already attached
      if (!this.scrollContainer) {
        this.scrollContainer = window as any;
        window.addEventListener('scroll', this.scrollHandler!);
        window.addEventListener('resize', this.resizeHandler!);
      }

      // Calculate grid dimensions
      const gridWidth = grid.offsetWidth;
      const itemMinWidth = getItemMinWidth();
      const gridGap = getGridGap();
      const itemsPerRow = Math.max(1, Math.floor(gridWidth / (itemMinWidth + gridGap)));
      const itemWidth = (gridWidth - (itemsPerRow - 1) * gridGap) / itemsPerRow;
      const itemHeight = itemWidth * ITEM_ASPECT_RATIO;
      const rowHeight = itemHeight + gridGap;

      // Calculate visible range
      const scrollTop = window.scrollY;
      const viewportHeight = window.innerHeight;
      const gridTop = grid.getBoundingClientRect().top + scrollTop;
      const scrollIntoGrid = Math.max(0, scrollTop - gridTop);

      // Get groups for calculations that account for headers
      const allGroups = this.photoService.getPhotoGroups();

      // Use scroll position to find visual row, accounting for header heights
      const rawFirstVisibleRow = this.scrollPositionToVisualRow(
        scrollIntoGrid,
        rowHeight,
        allGroups,
        itemsPerRow
      );
      const firstVisibleRow = Math.max(0, rawFirstVisibleRow - BUFFER_ROWS);

      const rawLastVisibleRow = this.scrollPositionToVisualRow(
        scrollIntoGrid + viewportHeight,
        rowHeight,
        allGroups,
        itemsPerRow
      );
      const lastVisibleRow = rawLastVisibleRow + BUFFER_ROWS;
      const startIndex = this.visualRowToPhotoIndex(firstVisibleRow, itemsPerRow, allGroups);
      const endIndex = Math.min(
        this.totalPhotoCount,
        this.visualRowToPhotoIndex(lastVisibleRow, itemsPerRow, allGroups)
      );

      // Fetch visible photos (thumbnails load in background)
      const visiblePhotos = await this.photoService.getPhotos(startIndex, endIndex);

      // Check if any photos are missing thumbnails
      const hasMissingThumbnails = visiblePhotos.some((p) => !p.thumbnailUrl);

      // If thumbnails are loading, schedule a refresh to pick them up
      if (hasMissingThumbnails) {
        if (this.thumbnailRefreshTimer) {
          clearTimeout(this.thumbnailRefreshTimer);
        }
        this.thumbnailRefreshTimer = setTimeout(() => {
          this.renderVisibleItems();
        }, 500); // Refresh after 500ms to pick up loaded thumbnails
      }

      // Get visible groups for header rendering
      const visibleGroups = this.getVisibleGroups(allGroups, startIndex, endIndex);

      // Calculate spacer heights using scroll position that accounts for headers
      const topSpacerHeight = this.visualRowToScrollPosition(
        firstVisibleRow,
        rowHeight,
        allGroups,
        itemsPerRow
      );

      // Render with spacers and headers
      grid.innerHTML = '';

      // Top spacer
      if (topSpacerHeight > 0) {
        const topSpacer = document.createElement('div');
        topSpacer.style.height = `${topSpacerHeight}px`;
        topSpacer.style.gridColumn = `1 / -1`;
        grid.appendChild(topSpacer);
      }

      // Render headers and photos interleaved
      let currentPhotoIndex = startIndex;

      visibleGroups.forEach(group => {
        // Render group header
        const header = this.createMonthHeader(group);
        grid.appendChild(header);

        // Calculate visible photos in this group
        const groupEndIndex = group.startIndex + group.count;
        const visibleGroupStart = Math.max(group.startIndex, startIndex);
        const visibleGroupEnd = Math.min(groupEndIndex, endIndex);

        // Render photos for this group
        const groupPhotos = visiblePhotos.slice(
          visibleGroupStart - startIndex,
          visibleGroupEnd - startIndex
        );

        groupPhotos.forEach((photo) => {
          const thumbnailDiv = this.createThumbnailElement(photo, currentPhotoIndex);
          grid.appendChild(thumbnailDiv);
          currentPhotoIndex++;
        });
      });

      // Bottom spacer - calculate total height and subtract what we've rendered
      const totalVisualRows = allGroups.reduce(
        (sum, g) => sum + Math.ceil(g.count / itemsPerRow),
        0
      );
      const totalScrollHeight = this.visualRowToScrollPosition(
        totalVisualRows,
        rowHeight,
        allGroups,
        itemsPerRow
      );

      // Calculate height of rendered content (headers + rows for visible groups)
      const renderedContentHeight = visibleGroups.reduce((sum, group) => {
        const groupEndIndex = group.startIndex + group.count;
        const visibleGroupStart = Math.max(group.startIndex, startIndex);
        const visibleGroupEnd = Math.min(groupEndIndex, endIndex);
        const visiblePhotosInGroup = visibleGroupEnd - visibleGroupStart;
        const visibleRowsInGroup = Math.ceil(visiblePhotosInGroup / itemsPerRow);
        return sum + HEADER_HEIGHT + visibleRowsInGroup * rowHeight;
      }, 0);

      const bottomSpacerHeight = totalScrollHeight - topSpacerHeight - renderedContentHeight;
      if (bottomSpacerHeight > 0) {
        const bottomSpacer = document.createElement('div');
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;
        bottomSpacer.style.gridColumn = `1 / -1`;
        grid.appendChild(bottomSpacer);
      }
    } finally {
      this.isRendering = false;
    }
  }

  private createThumbnailElement(photo: Photo, index: number): HTMLElement {
    const thumbnailDiv = document.createElement('div');
    thumbnailDiv.className = 'photo-thumbnail';

    const img = document.createElement('img');
    img.alt = photo.filePath;

    // Use thumbnail if available, otherwise placeholder
    img.src = photo.thumbnailUrl || PLACEHOLDER_IMAGE;

    // Add selection checkbox
    const checkbox = document.createElement('div');
    checkbox.className = 'thumbnail-select';
    const isSelected = this.selectionService.isSelected(photo.id);
    checkbox.textContent = isSelected ? '✓' : '';
    if (isSelected) {
      checkbox.classList.add('selected');
    }
    checkbox.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent lightbox from opening
      this.selectionService.toggleSelection(photo.id);
      this.updateCartBadge();
      // Update just this checkbox instead of re-rendering entire grid
      const nowSelected = this.selectionService.isSelected(photo.id);
      checkbox.textContent = nowSelected ? '✓' : '';
      checkbox.classList.toggle('selected', nowSelected);
    });
    thumbnailDiv.appendChild(checkbox);

    // Add star indicator if photo is starred
    if (photo.isStarred) {
      const starIndicator = document.createElement('div');
      starIndicator.className = 'thumbnail-star';
      starIndicator.textContent = '★';
      thumbnailDiv.appendChild(starIndicator);
    }

    // Add play button overlay if this is a video
    if (isVideo(photo)) {
      const playButton = document.createElement('div');
      playButton.className = 'video-play-button';

      // Create SVG using DOM methods (more stable than innerHTML during virtual scrolling)
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 100 100');

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', '50');
      circle.setAttribute('cy', '50');
      circle.setAttribute('r', '45');
      circle.setAttribute('fill', 'rgba(0, 0, 0, 0.6)');
      circle.setAttribute('stroke', 'white');
      circle.setAttribute('stroke-width', '2');

      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      polygon.setAttribute('points', '40,30 40,70 70,50');
      polygon.setAttribute('fill', 'white');

      svg.appendChild(circle);
      svg.appendChild(polygon);
      playButton.appendChild(svg);

      thumbnailDiv.appendChild(playButton);
    }

    // Add click handler to show lightbox
    thumbnailDiv.addEventListener('click', () => {
      this.showLightbox(index);
    });

    thumbnailDiv.appendChild(img);
    return thumbnailDiv;
  }

  /**
   * Format month and year for display
   */
  private formatMonthYear(month: number, year: number): string {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${monthNames[month]} ${year}`;
  }

  /**
   * Map scroll position to visual row number, accounting for headers between groups
   * This is the inverse of calculating pixel position from row number
   */
  private scrollPositionToVisualRow(
    scrollPosition: number,
    rowHeight: number,
    groups: PhotoGroup[],
    itemsPerRow: number
  ): number {
    let currentPosition = 0;
    let currentVisualRow = 0;

    for (const group of groups) {
      // Add header height
      currentPosition += HEADER_HEIGHT;

      // Check if scroll position is within this header
      if (scrollPosition < currentPosition) {
        return currentVisualRow;
      }

      // Calculate rows in this group
      const groupRows = Math.ceil(group.count / itemsPerRow);
      const groupRowsHeight = groupRows * rowHeight;

      // Check if scroll position is within this group's rows
      if (scrollPosition < currentPosition + groupRowsHeight) {
        const positionInGroup = scrollPosition - currentPosition;
        const rowsIntoGroup = Math.floor(positionInGroup / rowHeight);
        return currentVisualRow + rowsIntoGroup;
      }

      currentPosition += groupRowsHeight;
      currentVisualRow += groupRows;
    }

    return currentVisualRow;
  }

  /**
   * Calculate the scroll position for a given visual row
   * Accounts for headers between groups
   */
  private visualRowToScrollPosition(
    visualRow: number,
    rowHeight: number,
    groups: PhotoGroup[],
    itemsPerRow: number
  ): number {
    let currentPosition = 0;
    let currentVisualRow = 0;

    for (const group of groups) {
      // Add header height
      currentPosition += HEADER_HEIGHT;

      const groupRows = Math.ceil(group.count / itemsPerRow);

      // Check if target row is within this group
      if (currentVisualRow + groupRows > visualRow) {
        const rowsIntoGroup = visualRow - currentVisualRow;
        return currentPosition + rowsIntoGroup * rowHeight;
      }

      currentPosition += groupRows * rowHeight;
      currentVisualRow += groupRows;
    }

    return currentPosition;
  }

  /**
   * Map visual row number to photo index, accounting for incomplete rows in groups
   * This is critical for virtual scrolling to work correctly when groups have incomplete last rows
   */
  private visualRowToPhotoIndex(
    visualRow: number,
    itemsPerRow: number,
    groups: PhotoGroup[]
  ): number {
    let currentVisualRow = 0;
    let photoIndex = 0;

    for (const group of groups) {
      // Calculate how many visual rows this group occupies (ceil for incomplete rows)
      const groupRows = Math.ceil(group.count / itemsPerRow);

      // Check if the target visual row is within this group
      if (currentVisualRow + groupRows > visualRow) {
        // The visual row falls within this group
        const rowOffsetInGroup = visualRow - currentVisualRow;
        const photosInCompletedRows = rowOffsetInGroup * itemsPerRow;
        return group.startIndex + Math.min(photosInCompletedRows, group.count);
      }

      // Move to next group
      currentVisualRow += groupRows;
      photoIndex = group.startIndex + group.count;
    }

    // If we're past all groups, return total count
    return this.totalPhotoCount;
  }

  /**
   * Calculate which photo groups are visible in current viewport
   */
  private getVisibleGroups(
    groups: PhotoGroup[],
    startIndex: number,
    endIndex: number
  ): PhotoGroup[] {
    return groups.filter(group => {
      const groupEndIndex = group.startIndex + group.count;
      // Group is visible if it overlaps with [startIndex, endIndex)
      return groupEndIndex > startIndex && group.startIndex < endIndex;
    });
  }

  /**
   * Create a month/year header element
   */
  private createMonthHeader(group: PhotoGroup): HTMLElement {
    const header = document.createElement('div');
    header.className = 'month-header';

    const titleSpan = document.createElement('span');
    titleSpan.className = 'month-header-title';
    titleSpan.textContent = this.formatMonthYear(group.month, group.year);

    const countSpan = document.createElement('span');
    countSpan.className = 'month-header-count';
    countSpan.textContent = `${group.count} ${group.count === 1 ? 'photo' : 'photos'}`;

    header.appendChild(titleSpan);
    header.appendChild(countSpan);

    return header;
  }

  private pollExifProgress(): void {
    // Don't start a new polling loop if one is already running
    if (this.isPollingExif) {
      return;
    }

    const progressContainer = this.container.querySelector('.exif-progress') as HTMLElement;
    if (!progressContainer) return;

    this.isPollingExif = true;

    // Create progress UI once to avoid recreating HTML every second
    progressContainer.innerHTML = `
      <div class="exif-progress-text">
        Reading photo metadata <span class="exif-count"></span>
        <a href="#" class="exif-refresh-link">Refresh</a>
      </div>
      <div class="exif-warning" style="display: none; color: #ff6b35; font-weight: bold; margin-top: 0.5rem;">
        ⚠️ Keep this tab visible! Browser may throttle or pause when tab is hidden.
      </div>
      <div class="exif-tip" style="margin-top: 0.5rem; font-size: 0.9em; opacity: 0.8;">
        💡 Your system will not sleep during extraction. If interrupted, refresh to resume.
      </div>
    `;

    const countSpan = progressContainer.querySelector('.exif-count') as HTMLElement;
    const refreshLink = progressContainer.querySelector('.exif-refresh-link') as HTMLElement;
    const warningDiv = progressContainer.querySelector('.exif-warning') as HTMLElement;

    // Add click handler once (not every second!)
    refreshLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.loadPhotos();
    });

    // Add visibility change detection to show warning when page becomes hidden
    const handleVisibilityChange = () => {
      if (warningDiv) {
        warningDiv.style.display = document.hidden ? 'block' : 'none';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const checkProgress = () => {
      const progress = this.photoService.getExifExtractionProgress();

      if (progress && progress.isRunning) {
        // Show progress indicator and update count (avoid recreating HTML)
        progressContainer.style.display = 'block';
        if (countSpan) {
          countSpan.textContent = `${progress.completed} / ${progress.total}`;
        }

        // Check visibility state
        handleVisibilityChange();

        setTimeout(checkProgress, 1000); // Poll every second
      } else {
        // Check if we need to show reload prompt due to conflict
        if (this.photoService.needsReloadFromConflict()) {
          progressContainer.style.display = 'block';
          progressContainer.innerHTML = `
            <div class="exif-progress-text" style="color: #ff6b35;">
              ⚠️ Photos were updated on another device.
              <button class="reload-btn" style="margin-left: 1rem; padding: 0.5rem 1rem; cursor: pointer;">
                Reload to sync
              </button>
            </div>
          `;
          const reloadBtn = progressContainer.querySelector('.reload-btn') as HTMLElement;
          reloadBtn?.addEventListener('click', () => {
            window.location.reload();
          });
        } else {
          // Hide progress indicator when done
          progressContainer.style.display = 'none';
        }

        this.isPollingExif = false; // Reset flag when polling stops

        // Remove visibility listener
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    setTimeout(checkProgress, 1000);
  }

  private initializeUI(): void {
    this.container.innerHTML = `
      <div class="app">
        <header class="app-header">
          <h1>Photo Library</h1>
          <div class="header-controls">
            <div class="filter-toggle">
              <button class="filter-btn filter-btn-all active" data-filter="all">All Photos</button>
              <button class="filter-btn filter-btn-starred" data-filter="starred">Starred</button>
            </div>
          </div>
        </header>
        <div class="exif-progress" style="display: none;"></div>
        <div class="photo-grid"></div>
      </div>
    `;

    // Setup filter toggle handlers
    const allBtn = this.container.querySelector('.filter-btn-all') as HTMLElement;
    const starredBtn = this.container.querySelector('.filter-btn-starred') as HTMLElement;

    allBtn?.addEventListener('click', () => this.setFilter(false));
    starredBtn?.addEventListener('click', () => this.setFilter(true));

    // Create floating cart button (fixed position, visible over lightbox)
    this.createFloatingCartButton();

    // Initialize cart badge
    this.updateCartBadge();
  }

  private createFloatingCartButton(): void {
    // Remove existing cart button if present
    document.querySelector('.cart-btn-floating')?.remove();

    const cartBtn = document.createElement('button');
    cartBtn.className = 'cart-btn-floating';
    cartBtn.setAttribute('aria-label', 'Download cart');
    cartBtn.style.display = 'none'; // Hidden until selections are made
    cartBtn.innerHTML = `
      <span class="cart-icon">⬇</span>
      <span class="cart-badge" style="display: none;"></span>
    `;
    cartBtn.addEventListener('click', () => this.toggleCartPanel());
    document.body.appendChild(cartBtn);
  }

  private async setFilter(showOnlyStarred: boolean): Promise<void> {
    this.photoService.setStarFilter(showOnlyStarred);

    // Update button states
    const allBtn = this.container.querySelector('.filter-btn-all') as HTMLElement;
    const starredBtn = this.container.querySelector('.filter-btn-starred') as HTMLElement;

    if (showOnlyStarred) {
      allBtn?.classList.remove('active');
      starredBtn?.classList.add('active');
    } else {
      allBtn?.classList.add('active');
      starredBtn?.classList.remove('active');
    }

    // Update total count and re-render
    this.totalPhotoCount = this.photoService.getTotalCount();
    window.scrollTo(0, 0);
    await this.renderVisibleItems();
  }

  private updateCartBadge(): void {
    const cartBtn = document.querySelector('.cart-btn-floating') as HTMLElement;
    const badge = cartBtn?.querySelector('.cart-badge') as HTMLElement;
    const count = this.selectionService.getSelectionCount();

    // Show/hide the entire button based on selection count
    if (cartBtn) {
      cartBtn.style.display = count > 0 ? 'flex' : 'none';
    }

    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  private toggleCartPanel(): void {
    let panel = document.querySelector('.cart-panel') as HTMLElement;
    if (!panel) {
      panel = this.createCartPanel();
      document.body.appendChild(panel);
    }

    const isOpen = panel.classList.contains('open');
    if (isOpen) {
      panel.classList.remove('open');
    } else {
      this.updateCartPanelContent();
      panel.classList.add('open');
    }
  }

  private createCartPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'cart-panel';
    panel.innerHTML = `
      <div class="cart-panel-header">
        <h2>Download Cart</h2>
        <button class="cart-panel-close" aria-label="Close">&times;</button>
      </div>
      <div class="cart-panel-content"></div>
      <div class="cart-panel-footer">
        <div class="cart-total"></div>
        <button class="cart-download-btn">Download ZIP</button>
        <button class="cart-clear-btn">Clear All</button>
      </div>
    `;

    // Close button
    const closeBtn = panel.querySelector('.cart-panel-close') as HTMLElement;
    closeBtn.addEventListener('click', () => panel.classList.remove('open'));

    // Download button
    const downloadBtn = panel.querySelector('.cart-download-btn') as HTMLElement;
    downloadBtn.addEventListener('click', () => this.downloadSelected());

    // Clear button
    const clearBtn = panel.querySelector('.cart-clear-btn') as HTMLElement;
    clearBtn.addEventListener('click', () => {
      this.selectionService.clearSelection();
      this.updateCartBadge();
      this.updateCartPanelContent();
      // Grid checkboxes will update naturally on next scroll
    });

    return panel;
  }

  private cartPanelRefreshTimer?: ReturnType<typeof setTimeout>;

  private async updateCartPanelContent(): Promise<void> {
    const panel = document.querySelector('.cart-panel');
    if (!panel) return;

    const content = panel.querySelector('.cart-panel-content') as HTMLElement;
    const footer = panel.querySelector('.cart-panel-footer') as HTMLElement;
    const totalDiv = panel.querySelector('.cart-total') as HTMLElement;
    const selectedIds = this.selectionService.getSelectedIds();

    if (selectedIds.size === 0) {
      content.innerHTML = '<div class="cart-empty">No items selected</div>';
      footer.style.display = 'none';
      return;
    }

    footer.style.display = 'block';

    // Get only the selected photos (this triggers thumbnail loading for just these photos)
    const selectedPhotos = await this.photoService.getPhotosByIds(selectedIds);

    // Check if any selected photos are missing thumbnails
    const hasMissingThumbnails = selectedPhotos.some(p => !p.thumbnailUrl);

    // If thumbnails are loading, schedule a refresh to pick them up
    if (hasMissingThumbnails) {
      if (this.cartPanelRefreshTimer) {
        clearTimeout(this.cartPanelRefreshTimer);
      }
      this.cartPanelRefreshTimer = setTimeout(() => {
        // Only refresh if panel is still open
        if (panel.classList.contains('open')) {
          this.updateCartPanelContent();
        }
      }, 500);
    }

    // Calculate total size
    const totalSize = selectedPhotos.reduce((sum, p) => sum + p.fileSize, 0);
    totalDiv.textContent = `${selectedPhotos.length} items (${this.formatFileSize(totalSize)})`;

    // Build content
    content.innerHTML = '';
    for (const photo of selectedPhotos) {
      const item = document.createElement('div');
      item.className = 'cart-item';

      const thumb = document.createElement('div');
      thumb.className = 'cart-item-thumb';
      if (photo.thumbnailUrl) {
        const img = document.createElement('img');
        img.src = photo.thumbnailUrl;
        thumb.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'cart-item-info';
      const fileName = photo.filePath.split('/').pop() || photo.filePath;
      const nameDiv = document.createElement('div');
      nameDiv.className = 'cart-item-name';
      nameDiv.textContent = fileName;
      const sizeDiv = document.createElement('div');
      sizeDiv.className = 'cart-item-size';
      sizeDiv.textContent = this.formatFileSize(photo.fileSize);
      info.appendChild(nameDiv);
      info.appendChild(sizeDiv);

      const removeBtn = document.createElement('button');
      removeBtn.className = 'cart-item-remove';
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', () => {
        this.selectionService.deselect(photo.id);
        this.updateCartBadge();
        this.updateCartPanelContent();
        // Grid checkboxes will update naturally on next scroll
      });

      item.appendChild(thumb);
      item.appendChild(info);
      item.appendChild(removeBtn);
      content.appendChild(item);
    }
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  private async downloadSelected(): Promise<void> {
    const selectedIds = this.selectionService.getSelectedIds();
    if (selectedIds.size === 0) return;

    // Get selected photos
    const allPhotos = await this.photoService.getPhotos(0, this.totalPhotoCount);
    const selectedPhotos = allPhotos.filter(p => selectedIds.has(p.id));

    // Show progress modal
    const modal = this.createDownloadProgressModal(selectedPhotos.length);
    document.body.appendChild(modal);

    let cancelled = false;
    const cancelBtn = modal.querySelector('.download-cancel-btn') as HTMLElement;
    cancelBtn.addEventListener('click', () => {
      cancelled = true;
    });

    try {
      const files: { [name: string]: Uint8Array } = {};
      const progressText = modal.querySelector('.download-progress-text') as HTMLElement;
      const progressBar = modal.querySelector('.download-progress-bar') as HTMLElement;

      // Fetch files in parallel batches of 3
      const batchSize = 3;
      let completed = 0;

      for (let i = 0; i < selectedPhotos.length; i += batchSize) {
        if (cancelled) {
          throw new Error('Download cancelled');
        }

        const batch = selectedPhotos.slice(i, i + batchSize);
        const batchPromises = batch.map(async (photo) => {
          // Find the index in the current filtered photos array
          const allFilteredPhotos = await this.photoService.getPhotos(0, this.totalPhotoCount);
          const photoIndex = allFilteredPhotos.findIndex(p => p.id === photo.id);
          if (photoIndex === -1) {
            throw new Error(`Photo not found: ${photo.filePath}`);
          }

          const url = await this.photoService.getFullSizeUrl(photoIndex);
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to download ${photo.filePath}: HTTP ${response.status}`);
          }
          const arrayBuffer = await response.arrayBuffer();

          // Use filename from path, ensure uniqueness
          let fileName = photo.filePath.split('/').pop() || `photo_${photoIndex}`;
          // Handle duplicate filenames by adding index
          if (files[fileName]) {
            const ext = fileName.lastIndexOf('.');
            if (ext > 0) {
              fileName = `${fileName.substring(0, ext)}_${completed}${fileName.substring(ext)}`;
            } else {
              fileName = `${fileName}_${completed}`;
            }
          }

          return { fileName, data: new Uint8Array(arrayBuffer) };
        });

        const results = await Promise.all(batchPromises);
        for (const { fileName, data } of results) {
          files[fileName] = data;
          completed++;
          progressText.textContent = `Downloading ${completed} of ${selectedPhotos.length}...`;
          progressBar.style.width = `${(completed / selectedPhotos.length) * 100}%`;
        }
      }

      if (cancelled) {
        throw new Error('Download cancelled');
      }

      // Create ZIP
      progressText.textContent = 'Creating ZIP file...';
      const zipData = zipSync(files);

      // Trigger download (use slice to get a fresh ArrayBuffer that TypeScript accepts)
      const blob = new Blob([zipData.slice().buffer], { type: 'application/zip' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `photos_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      // Close panel and clear selection
      const panel = document.querySelector('.cart-panel') as HTMLElement;
      if (panel) {
        panel.classList.remove('open');
      }
      this.selectionService.clearSelection();
      this.updateCartBadge();
      // Grid checkboxes will update naturally on next scroll

    } catch (error) {
      if ((error as Error).message !== 'Download cancelled') {
        console.error('Download error:', error);
        alert(`Download failed: ${(error as Error).message}`);
      }
    } finally {
      modal.remove();
    }
  }

  private createDownloadProgressModal(totalCount: number): HTMLElement {
    const modal = document.createElement('div');
    modal.className = 'download-modal';
    modal.innerHTML = `
      <div class="download-modal-content">
        <h3>Preparing Download</h3>
        <div class="download-progress-text">Downloading 0 of ${totalCount}...</div>
        <div class="download-progress-track">
          <div class="download-progress-bar" style="width: 0%"></div>
        </div>
        <button class="download-cancel-btn">Cancel</button>
      </div>
    `;
    return modal;
  }
}
